import Vue from "vue";
import Router, { Route } from "vue-router";
import { getModule } from "vuex-module-decorators";
import Layout from "@/layout/index.vue";
import AuthenticationService from "../security/AuthenticationService";
import ActivityBasedTimer from "@/lib/activityBasedTimer";
import notifications from "@/lib/notifications";
import permissions from "@/security/permissions";
import store from "@/store/index";

import {
  HOME_SHORTCUT,
  ORDER_SHORTCUT,
  TRUCK_SHORTCUT,
  DISPATCH_SHORTCUT,
  TRUCK_SUMMARY_SHORTCUT,
  BARCODE_INVENTORY_SUMMARY_SHORTCUT,
  BARCODE_INVENTORY_ITEMS_SHORTCUT,
  BARCODE_PALLETS_SHORTCUT,
  BARCODE_LOADS_SHORTCUT,
  BARCODE_ORDER_SEARCH_SHORTCUT,
  STOCK_ORDER_SEARCH_SHORTCUT,
  STOCK_ADJUSTMENT_SEARCH_SHORTCUT,
  BARCODE_MANIFESTS_SHORTCUT,
  BARCODE_BULK_INVENTORY_ITEMS_SHORTCUT
} from "@/consts";
import { SettingModule } from "@/store/modules";
import moment from "moment";

Vue.use(Router);

const versioningTimer = ActivityBasedTimer();
var isNewVersionNotificationShown = false;

const rootPath = {
  text: "Home",
  to: "/home"
};

const adminRootPath = { text: "Admin", disabled: true };
const agentRootPath = { text: "Agent", disabled: true };
const barcodeSystemRootPath = { text: "Barcode", disabled: true };
const documentsRootPath = { text: "Document Management", to: "/dms/batch" };
const paperlessRootPath = { text: "Paperless Management", to: "/paperless/batch" };
const toolsRootPath = { text: "Tools", disabled: true };
export const constantRoutes = [
  {
    path: "/",
    component: Layout,
    redirect: "/home",
    children: [
      {
        path: "/home",
        component: () => import("@/views/Home.vue"),
        name: "Home",
        meta: {
          title: "Home",
          icon: "home",
          affix: true,
          routePath: [rootPath],
          actions: HOME_SHORTCUT
        }
      },
      {
        path: "/admin/drivers",
        component: () => import("@/views/admin/Drivers.vue"),
        name: "Drivers",
        meta: {
          title: "Drivers",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Drivers" }]
        }
      },
      {
        path: "/admin/orderTypes",
        component: () => import("@/views/admin/OrderTypes.vue"),
        name: "Order Type Maintenance",
        meta: {
          title: "OrderTypes",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Order Type Maintenance" }]
        }
      },
      {
        path: "/admin/truckZip3OverridesMaintenance",
        component: () => import("@/views/admin/TruckZip3Overrides.vue"),
        name: "TruckZip3Overrides",
        meta: {
          title: "Truck Zip3 Overrides",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Truck Zip3 Overrides" }]
        }
      },
      {
        path: "/admin/truckZip3OverridesMaintenance/new",
        name: "NewTruckZip3OverridesMaintenance",
        component: () => import("@/views/admin/NewTruckZip3Overrides.vue"),
        meta: {
          title: "New Truck Zip3 Overrides Maintenance",
          icon: "Truck",
          actions: [],
          affix: true,
          routePath: [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Truck Zip3 Overrides Maintenance",
              to: "/admin/truckZip3OverridesMaintenance"
            },
            {
              text: "New Truck Zip3 Overrides Maintenance",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/admin/truckZip3OverridesMaintenance/:id",
        name: "UpdateTruckZip3OverridesMaintenance",
        component: () => import("@/views/admin/NewTruckZip3Overrides.vue"),
        meta: {
          title: "Update Truck Zip3 Override Maintenance",
          icon: "Update Truck Zip3 Override Maintenance",
          actions: TRUCK_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Truck Zip3 Overrides",
              to: "/admin/truckZip3OverridesMaintenance"
            },
            {
              text: "Edit",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/admin/truckTemplate",
        component: () => import("@/views/admin/TruckTemplates.vue"),
        name: "TruckTemplate",
        meta: {
          title: "Truck Template",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Truck Template" }]
        }
      },
      {
        path: "/truckTemplates/new",
        name: "NewTruckTemplate",
        component: () => import("@/views/admin/NewTruckTemplate.vue"),
        meta: {
          title: "New Truck Template",
          icon: "Truck",
          actions: [],
          affix: true,
          routePath: [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Truck Template",
              to: "/admin/truckTemplate"
            },
            {
              text: "New Truck Template",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/admin/truckTemplates/:id",
        name: "Truck Template Information",
        component: () => import("@/views/admin/NewTruckTemplate.vue"),
        meta: {
          title: "Truck Template",
          icon: "Truck Template",
          actions: TRUCK_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Truck Template",
              to: "/admin/truckTemplate"
            },
            {
              text: "Edit",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/admin/timeframeSchedulingSettings",
        component: () => import("@/views/TimeframeSchedulingFacilitySettings.vue"),
        name: "Timeframe Scheduling Settings",
        meta: {
          title: "TimeframeSchedulingFacilitySettings",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Timeframe Scheduling Settings" }]
        }
      },
      {
        path: "/admin/billToCommodities",
        component: () => import("@/views/admin/BillToCommodities.vue"),
        name: "Bill To - Commodities",
        meta: {
          title: "BillToCommodities",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Bill To - Commodities" }]
        }
      },
      {
        path: "/admin/billToManufacturers",
        component: () => import("@/views/admin/BillToManufacturers.vue"),
        name: "BillToManufacturers",
        meta: {
          title: "Bill To - Manufacturers",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Bill To - Manufacturers" }]
        }
      },
      {
        path: "/admin/commodityUnloadDefaults",
        component: () => import("@/views/admin/CommodityUnloadDefaults.vue"),
        name: "Commodity Unload Defaults",
        meta: {
          title: "CommodityUnloadDefaults",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Commodity Unload Defaults" }]
        }
      },
      {
        path: "/dms/batch",
        component: () => import("@/views/documents/Batches.vue"),
        name: "Batches",
        meta: {
          title: "Batch Indexing",
          affix: true,
          routePath: [rootPath, documentsRootPath]
        }
      },
      {
        path: "/dms/batch/:workItemId",
        component: () => import("@/views/documents/BatchIndex.vue"),
        name: "Batch Indexing",
        meta: {
          title: "Batch Indexing",
          affix: true,
          routePath: 
          [
            rootPath, documentsRootPath, 
            { 
              text: "Batch Indexing"
            }
          ]
        }
      },
      {
        path: "/asn/map/:uploadId?",
        component: () => import("@/views/asn/MapAsn.vue"),
        name: "AsnMap",
        meta: {
          title: "ASN Map",
          affix: true,
          routePath: [
            rootPath, 
            { text: "ASN Map" }
          ]
        }
      },
      {
        path: "/paperless/batch",
        component: () => import("@/views/paperless/Batches.vue"),
        name: "Paperless",
        meta: {
          title: "Paperless Documents",
          affix: true,
          routePath: [rootPath, paperlessRootPath]
        }
      },
      {
        path: "/paperless/batch/:workItemId",
        component: () => import("@/views/paperless/BatchesIndex.vue"),
        name: "Paperless Documents Details",
        meta: {
          title: "Paperless Documents Details",
          affix: true,
          routePath: 
          [
            rootPath, paperlessRootPath, 
            { 
              text: "Paperless Documents", 
              to: "/paperless/batch"
            },
            { 
              text: "Details"
            }
            ]
        }
      },
      {
        path: "/admin/groups/create",
        component: () => import("@/views/admin/GroupEdit.vue"),
        name: "GroupCreate",
        meta: {
          title: "Create Group",
          affix: true,
          routePath: [
            rootPath,
            adminRootPath,
            { text: "Groups", to: "/admin/groups" },
            { text: "Create" }
          ]
        }
      },
      {
        path: "/admin/groups/:id",
        component: () => import("@/views/admin/GroupEdit.vue"),
        name: "GroupEdit",
        meta: {
          title: "Edit Group",
          icon: "home",
          affix: true,
          routePath: [
            rootPath,
            adminRootPath,
            { text: "Groups", to: "/admin/groups" },
            { text: "Edit" }
          ]
        }
      },
      {
        path: "/admin/users/:externalId",
        component: () => import("@/views/admin/AgentUserEdit.vue"),
        name: "AgentUserEdit",
        meta: {
          title: "Edit User",
          icon: "home",
          affix: true,
          routePath: [
            rootPath,
            adminRootPath,
            { text: "Users", to: "/admin/users" },
            { text: "Edit" }
          ]
        }
      },
      {
        path: "/admin/facilities",
        component: () => import("@/views/admin/Facilities.vue"),
        name: "Users2",
        meta: {
          title: "Facilities",
          icon: "home",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Facilities" }]
        }
      },
      {
        path: "/admin/facility/create",
        component: () => import("@/views/admin/FacilityEdit.vue"),
        name: "FacilityCreate",
        meta: {
          title: "Create Facility",
          icon: "home",
          affix: true,
          routePath: [
            rootPath,
            adminRootPath,
            { text: "Facilities", to: "/admin/facilities" },
            { text: "Create" }
          ]
        }
      },
      {
        path: "/admin/facility/:facilityCode",
        component: () => import("@/views/admin/FacilityEdit.vue"),
        name: "FacilityEdit",
        meta: {
          title: "Edit Facility",
          icon: "home",
          affix: true,
          routePath: [
            rootPath,
            adminRootPath,
            { text: "Facilities", to: "/admin/facilities" },
            { text: "Edit" }
          ]
        }
      },
      {
        path: "/admin/groups",
        component: () => import("@/views/admin/Groups.vue"),
        name: "Groups",
        meta: {
          title: "Groups",
          icon: "home",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Groups" }]
        }
      },
      {
        path: "/admin/roles/create",
        component: () => import("@/views/admin/RoleEdit.vue"),
        name: "RolesCreate",
        meta: {
          title: "Create Role",
          affix: true,
          routePath: [
            rootPath,
            adminRootPath,
            { text: "Roles", to: "/admin/roles" },
            { text: "Create" }
          ]
        }
      },
      {
        path: "/admin/users",
        component: () => import("@/views/admin/AgentUsers.vue"),
        name: "agentUsers",
        meta: {
          title: "Users",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Users" }]
        }
      },
      {
        path: "/admin/roles/:id",
        component: () => import("@/views/admin/RoleEdit.vue"),
        name: "RoleEdit",
        meta: {
          title: "Edit Role",
          affix: true,
          routePath: [
            rootPath,
            adminRootPath,
            { text: "Roles", to: "/admin/roles" },
            { text: "Edit" }
          ]
        }
      },
      {
        path: "/admin/roles",
        component: () => import("@/views/admin/Roles.vue"),
        name: "Roles",
        meta: {
          title: "Roles",
          icon: "home",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Roles" }]
        }
      },
      {
        path: "/admin/agents/:id",
        component: () => import("@/views/admin/AgentEdit.vue"),
        name: "Edit Agents",
        meta: {
          title: "Agents",
          icon: "home",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Edit Agent" }]
        }
      },      
      {
        path: "/admin/agents",
        component: () => import("@/views/admin/Agents.vue"),
        name: "Agents",
        meta: {
          title: "Agents",
          icon: "home",
          affix: true,
          routePath: [rootPath, adminRootPath, { text: "Agents" }]
        }
      },      
      {
        path: "/dispatch",
        component: () => import("@/views/Orders.vue"),
        name: "Dispatch",
        meta: {
          title: "Dispatch",
          icon: "dispatch",
          actions: DISPATCH_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            {
              text: "Dispatch",
              to: "dispatch"
            }
          ]
        }
      },
      {
        path: "/orders/advanced-search",
        component: () => import("@/views/AdvancedSearchResults.vue"),
        name: "AdvancedSearchResults",
        meta: {
          title: "Advanced Search Results",
          icon: "Order",
          affix: true,
          routePath: [
            rootPath,
            { text: "Dispatch", to: "/dispatch" },
            { text: "Advanced Search Results" }
          ],
          actions: HOME_SHORTCUT
        }
      },
      {
        path: "/orders/search",
        component: () => import("@/views/SearchResults.vue"),
        name: "SearchResults",
        meta: {
          title: "Search Results",
          icon: "Order",
          affix: true,
          routePath: [rootPath, { text: "Dispatch", to: "/dispatch" }, { text: "Search Results" }],
          actions: HOME_SHORTCUT
        }
      },
      {
        path: "/orders/new",
        component: () => import("@/views/NewOrder.vue"),
        name: "NewOrder",
        meta: {
          title: "New Order",
          icon: "Order",
          affix: true,
          routePath: [
            rootPath,
            { text: "Dispatch", to: "/dispatch" },
            { text: "New Order", to: "NewOrder" }
          ]
        }
      },
      {
        path: "/order/:idx/ships-with",
        component: () => import("@/views/ShipsWithOrders.vue"),
        name: "ConsolidateOrders",
        meta: {
          title: "Ships with Orders",
          icon: "Ships with Orders",
          affix: true,
          generateDynamicPath: (route: Route) => [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Order",
              to: `/order/${route.params.idx}`
            },
            {
              text: "Ships with Orders",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/order/:idx",
        component: () => import("@/views/Order.vue"),
        name: "Order",
        meta: {
          title: "Order",
          icon: "Order",
          actions: ORDER_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch",

            },
            {
              text: "Order",
              path: "/order/:idx"
            }
          ]
        }
      },
      {
        path: "/reports",
        component: () => import("@/views/Reports.vue"),
        name: "Reports",
        meta: {
          title: "Reports",
          icon: "Reports",
          affix: true,
          routePath: [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Reports",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/customerOnHold",
        component: () => import("@/views/CustomerOnHold.vue"),
        name: "Customers On Hold",
        meta: {
          title: "Customers On Hold",
          affix: true,
          requiresPermission: permissions.manage.customerOnHold.view,
          routePath: [rootPath, toolsRootPath, { text: "Customers On Hold" }]
        }
      },
      {
        path: "/trucks/:idx/combine",
        component: () => import("@/views/CombineTrucks.vue"),
        name: "Combine Truck",
        meta: {
          title: "Combine Truck",
          icon: "Truck",
          actions: TRUCK_SHORTCUT,
          affix: true,
          generateDynamicPath: (route: Route) => [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Truck Summary",
              to: "/trucks"
            },
            {
              text: "Truck",
              to: `/trucks/${route.params.idx}`
            },
            {
              text: "Combine Trucks",
              to: "#"
            }
          ],
          routePath: null
        }
      },
      {
        path: "/trucks/:idx/optimize",
        component: () => import("@/views/OptimizeTruck.vue"),
        name: "Optimize Truck",
        meta: {
          title: "Optimize Truck",
          icon: "Truck",
          actions: TRUCK_SHORTCUT,
          affix: true,
          generateDynamicPath: (route: Route) => [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Truck Summary",
              to: "/trucks"
            },
            {
              text: "Truck",
              to: `/trucks/${route.params.idx}`
            },
            {
              text: "Scheduled Timeframe Optimization",
              to: "#"
            }
          ],
          routePath: null
        }
      },
      {
        path: "/trucks/:idx/split",
        component: () => import("@/views/SplitTruck.vue"),
        name: "Split Truck",
        meta: {
          title: "Split Truck",
          icon: "Truck",
          actions: TRUCK_SHORTCUT,
          affix: true,
          generateDynamicPath: (route: Route) => [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Truck Summary",
              to: "/trucks"
            },
            {
              text: "Truck",
              to: `/trucks/${route.params.idx}`
            },
            {
              text: "Split Truck",
              to: "#"
            }
          ],
          routePath: null
        }
      },
      {
        path: "/trucks/import",
        name: "ImportTrucks",
        component: () => import("@/views/ImportTrucks.vue"),
        meta: {
          title: "Import Trucks",
          icon: "Truck",
          actions: [],
          affix: true,
          routePath: [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Truck Summary",
              to: "/trucks"
            },
            {
              text: "Import Trucks",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/trucks/new",
        name: "NewTruck",
        component: () => import("@/views/NewTruck.vue"),
        meta: {
          title: "New Truck",
          icon: "Truck",
          actions: [],
          affix: true,
          routePath: [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Truck Summary",
              to: "/trucks"
            },
            {
              text: "New Truck",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/trucks/commodityUnloadTimeOverrides",
        name: "CommodityUnloadTimeOverrides",
        component: () => import("@/views/CommodityUnloadOverrides.vue"),
        meta: {
          title: "Commodity Unload Time Overrides",
          icon: "Truck",
          actions: [],
          affix: true,
          routePath: [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Truck Summary",
              to: "/trucks"
            },
            {
              text: "Commodity Unload Time Overrides",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/trucks/:idx",
        name: "Truck Information",
        component: () => import("@/views/Truck.vue"),
        meta: {
          title: "Truck",
          icon: "Truck",
          actions: TRUCK_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Truck Summary",
              to: "/trucks"
            },
            {
              text: "Truck",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/trucks",
        name: "Truck Summary",
        component: () => import("@/views/Trucks.vue"),
        meta: {
          title: "Truck Summary",
          icon: "Truck",
          actions: TRUCK_SUMMARY_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Truck Summary",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/trucks/:idx/route",
        name: "Truck Routing",
        component: () => import("@/views/TruckRouting.vue"),
        meta: {
          title: "Truck Routing",
          icon: "Truck",
          actions: TRUCK_SHORTCUT,
          affix: true,
          generateDynamicPath: (route: Route) => [
            rootPath,
            {
              text: "Dispatch",
              to: "/dispatch"
            },
            {
              text: "Truck Summary",
              to: "/trucks"
            },
            {
              text: "Truck",
              to: `/trucks/${route.params.idx}`
            },
            {
              text: "Route Truck",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/barcode/inventory",
        name: "Inventory Summary",
        component: () => import("@/views/barcodeSystem/InventorySummary.vue"),
        meta: {
          title: "Inventory Summary",
          icon: "Truck",
          actions: BARCODE_INVENTORY_SUMMARY_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            barcodeSystemRootPath,
            {
              text: "Inventory Summary",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/barcode/inventoryItems",
        name: "Inventory Items",
        component: () => import("@/views/barcodeSystem/InventoryItems.vue"),
        meta: {
          title: "Inventory Items",
          icon: "Truck",
          actions: BARCODE_INVENTORY_ITEMS_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            barcodeSystemRootPath,
            {
              text: "Inventory Items",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/barcode/bulkInventoryItems",
        name: "Bulk Inventory Items",
        component: () => import("@/views/barcodeSystem/BulkInventoryItems.vue"),
        meta: {
          title: "Bulk Inventory Items",
          icon: "Truck",
          actions: BARCODE_BULK_INVENTORY_ITEMS_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            barcodeSystemRootPath,
            {
              text: "Bulk Inventory Items",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/barcode/pallets",
        name: "Pallets",
        component: () => import("@/views/barcodeSystem/Pallets.vue"),
        meta: {
          title: "Pallets",
          icon: "Truck",
          actions: BARCODE_PALLETS_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            barcodeSystemRootPath,
            {
              text: "Pallets",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/barcode/loads",
        name: "Loads",
        component: () => import("@/views/barcodeSystem/Loads.vue"),
        meta: {
          title: "Loads",
          icon: "Truck",
          actions: BARCODE_LOADS_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            barcodeSystemRootPath,
            {
              text: "Loads",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/barcode/manifests",
        name: "BarcodeManifests",
        component: () => import("@/views/barcodeSystem/Manifests.vue"),
        meta: {
          title: "Outbound Barcode Manifests",
          icon: "Truck",
          actions: BARCODE_MANIFESTS_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            barcodeSystemRootPath,
            {
              text: "Outbound Barcode Manifests",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/barcode/manifests/import",
        name: "BarcodeManifestImport",
        component: () => import("@/views/barcodeSystem/ImportManifests.vue"),
        meta: {
          title: "Import Outbound Barcode Manifests",
          affix: true,
          routePath: [
            rootPath,
            barcodeSystemRootPath,
            {
              text: "Import Outbound Barcode Manifests",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/barcode/orderSearch",
        name: "Barcode Order Search",
        component: () => import("@/views/barcodeSystem/BarcodeOrderSearch.vue"),
        meta: {
          title: "Barcode Order Search",
          icon: "Truck",
          actions: BARCODE_ORDER_SEARCH_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            barcodeSystemRootPath,
            {
              text: "Barcode Order Search",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/barcode/stockOrderSearch",
        name: "Inbound Stock Order Search",
        component: () => import("@/views/barcodeSystem/StockOrderSearch.vue"),
        meta: {
          title: "Inbound Stock Order Search",
          icon: "Truck",
          actions: STOCK_ORDER_SEARCH_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            barcodeSystemRootPath,
            {
              text: "Inbound Stock Order Search",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/barcode/stockOrders/new",
        name: "New Inbound Stock Order",
        component: () => import("@/views/barcodeSystem/NewStockOrder.vue"),
        meta: {
          title: "New Inbound Stock Order",
          affix: true,
          routePath: [
            rootPath,
            barcodeSystemRootPath,
            {
              text: "New Inbound Stock Order",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/barcode/stockAdjustmentSearch",
        name: "Stock Adjustment Search",
        component: () => import("@/views/barcodeSystem/StockAdjustmentSearch.vue"),
        meta: {
          title: "Stock Adjustment Search",
          icon: "Truck",
          actions: STOCK_ADJUSTMENT_SEARCH_SHORTCUT,
          affix: true,
          routePath: [
            rootPath,
            barcodeSystemRootPath,
            {
              text: "Stock Adjustment Search",
              to: "#"
            }
          ]
        }
      },
      {
        path: "/barcode/manufacturers",
        component: () => import("@/views/barcodeSystem/BarcodeManufacturers.vue"),
        name: "Barcode Manufacturers",
        meta: {
          title: "Barcode Manufacturers",
          affix: true,
          routePath: [rootPath, barcodeSystemRootPath, { text: "Manufacturers" }]
        }
      },
      {
        path: "/barcode/itemTypes",
        component: () => import("@/views/barcodeSystem/ItemTypes.vue"),
        name: "Item Types",
        meta: {
          title: "Item Types",
          affix: true,
          routePath: [rootPath, barcodeSystemRootPath, { text: "Item Types" }]
        }
      },
      {
        path: "/barcode/companies",
        component: () => import("@/views/barcodeSystem/BarcodeCompanies.vue"),
        name: "Barcode Companies",
        meta: {
          title: "Barcode Companies",
          affix: true,
          routePath: [rootPath, barcodeSystemRootPath, { text: "Companies" }]
        }
      },
      {
        path: "/barcode/damageCodes",
        component: () => import("@/views/barcodeSystem/DamageCodes.vue"),
        name: "Damage Codes",
        meta: {
          title: "Damage Codes",
          affix: true,
          routePath: [rootPath, barcodeSystemRootPath, { text: "Damage Codes" }]
        }
      },
      {
        path: "/barcode/returnReasons",
        component: () => import("@/views/barcodeSystem/ReturnReasons.vue"),
        name: "Return Reasons",
        meta: {
          title: "Return Reasons",
          affix: true,
          routePath: [rootPath, barcodeSystemRootPath, { text: "Return Reasons" }]
        }
      },
      {
        path: "/barcode/stockSkus",
        component: () => import("@/views/barcodeSystem/StockSkus.vue"),
        name: "Stock SKUs",
        meta: {
          title: "Stock SKUs",
          affix: true,
          routePath: [rootPath, barcodeSystemRootPath, { text: "Stock SKUs" }]
        }
      },
      {
        path: "/barcode/modelCubes",
        component: () => import("@/views/barcodeSystem/ModelCubes.vue"),
        name: "Model Cubes",
        meta: {
          title: "Model Cubes",
          affix: true,
          routePath: [rootPath, barcodeSystemRootPath, { text: "Model Cubes" }]
        }
      },
      {
        path: "/barcode/warehouseLocations",
        component: () => import("@/views/barcodeSystem/WarehouseLocations.vue"),
        name: "Warehouse Locations",
        meta: {
          title: "Warehouse Locations",
          affix: true,
          routePath: [rootPath, barcodeSystemRootPath, { text: "Warehouse Locations" }]
        }
      }
    ]
  },
/*
  {
    path: "/user",
    component: Layout,
    children: [
      {
        path: "/",
        component: () => import("@/views/User.vue"),
        name: "User",
        meta: {
          title: "User",
          icon: "user",
          affix: true,
          routePath: [
            rootPath,
            {
              text: "User",
              to: "#"
            }
          ]
        }
      }
    ]
  },
  */
  {
    path: "/callback",
    component: () => import("@/views/Callback.vue"),
    name: "Callback",
    meta: {
      allowAnonymous: true
    }
  },
  {
    path: "/signed-out",
    component: Layout,
    children: [
      {
        path: "/",
        component: () => import("@/views/SignedOut.vue"),
        name: "SignedOut",
        meta: {
          title: "Signed Out",
          affix: true,
          allowAnonymous: true,
          routePath: [
            rootPath,
            {
              text: "Signed Out",
              to: "#"
            }
          ]
        }
      }
    ]
  }
];

const createRouter = () =>
  new Router({
    mode: "history", // require service support
    routes: constantRoutes
  });

const router = createRouter();

export default router;

export const isNewerVersionAvailable = async () => {
const settingModule:SettingModule = getModule(SettingModule);

  const currentVersion = settingModule.currentVersion;
  const response = await settingModule.getVersion();
  const newVersion = response ? response.data : currentVersion;

  if (currentVersion !== newVersion) {
    settingModule.setNewVersionValue(newVersion);
    settingModule.setOldVersionValue(currentVersion);
  }
  const newVersionAvailable = !!currentVersion && currentVersion != newVersion;
  if (newVersionAvailable) {
    !!currentVersion && showToast();
    localStorage.setItem("new-version-timestamp", moment().format("YYYY/MM/DD HH:mm:ss"));
  }
};

export const showToast = () => {
  if (!isNewVersionNotificationShown) {
    notifications.showRefresh(
      "New application version available",
      "Click here to refresh page",
      null,
      true,
      true
    );
    isNewVersionNotificationShown = true;
  }
};

isNewerVersionAvailable();
setInterval(async () => {
  await isNewerVersionAvailable();
}, 1000 * 60 * process.env.VUE_APP_CHECK_VERSION_IN_MINUTES);

router.beforeEach((to, from, next) => {
  versioningTimer.runTimersCheck();
  const allowAnonymous = to.matched.some(record => record.meta.allowAnonymous);
  if (to.meta.generateDynamicPath) to.meta.routePath = to.meta.generateDynamicPath(to);
  if (!allowAnonymous) {
    AuthenticationService.getUser(true).then(user => {
      if (user) {
        const permissionRecord = to.matched.find(record => record.meta.requiresPermission);
        const requiredPermission = permissionRecord && permissionRecord.meta.requiresPermission;

        if (requiredPermission) {
          const accountPermissions = store.state["account"] && store.state["account"].permissions
            ? store.state["account"].permissions
            : [];
          const hasPermission = accountPermissions.indexOf(requiredPermission) > -1;
          if (!hasPermission) {
            notifications.error(
              "Unauthorized",
              "You are not authorized to access this page."
            );
            next("/home");
            return;
          }
        }

        next();
      }
    });
  } else {
    next();
  }
});

const DEFAULT_TITLE = "Home";

router.afterEach((to, from) => {
  const suffix = to.meta.title || DEFAULT_TITLE;
  document.title = `Vision OPS | ${suffix}`;
});
